import {AccountData, AccountDataStatusEnum, PortfolioPeriodEnum} from '../../api-client-kt/src';
import {countBy, groupBy, range, sumBy} from 'es-toolkit';
import {PortfolioSegmentDataContext} from './portfolio-segment-data-context.service';
import {ArrayUtils} from '../lib/arrays';
import {StringUtils} from '../lib/strings';
import {AppConstants} from '../app.constants';
import {MathArray, quantileSeq} from 'mathjs';
import {CheckEvaluation, CheckSeverity} from './app-data-checks';

export interface AccountPartitionStats {
    count: number;
    countPct: number;
    gnwpEur: number;
    gnwpEurPct: number;
}

export interface AccountChecksStats {
    id: string;
    name: string;
    severity: CheckSeverity;
    count: number;
}

export interface PortfolioSegmentStatistics {

    statsByCalculatedTier: { [tier: string]: AccountPartitionStats };
    statsByFinalTier: { [tier: string]: AccountPartitionStats };
    statsByLastYearTier: { [tier: string]: AccountPartitionStats };

    statsByHg: { [hg: number]: AccountPartitionStats };

    totalCount: number;
    totalGnwpEur: number;

    notRenewableCount: number;
    renewedCount: number;
    renewedGnwpEur: number;

    lostCancelledCount: number;
    newCount: number;
}

export interface AccountStatsBucket {
    min: number;
    max: number;
    count: number;
    gnwpEur: number;
}

export class AccountSegmentStatisticsCalculator {

    static NotTieredStatus = 'Not Tiered';

    static aggregatePartition<K extends PropertyKey>(
        accounts: AccountData[],
        partitionBy: (a: AccountData) => K,
        totalAccountsPreCalculated?: number,
        totalGnwpEurPreCalculated?: number
    ): Record<K, AccountPartitionStats> {
        const groups = groupBy(accounts, partitionBy) as Record<K, AccountData[]>;
        const retval = {} as Record<K, AccountPartitionStats>;

        const totalAccounts = totalAccountsPreCalculated ?? Math.max(accounts.length, 0.01);
        const totalGnwpEur = totalGnwpEurPreCalculated ?? Math.max(sumBy(accounts, i => i.gnwpEur ?? 0), 0.01);

        for (const entry of Object.entries(groups)) {
            const key = entry[0] as K;
            const group = entry[1] as AccountData[];
            const count = group.length;
            const countPct = count / totalAccounts;
            const gnwpEur = sumBy(group, i => i.gnwpEur ?? 1.);
            const gnwpEurPct = gnwpEur / totalGnwpEur;
            retval[key as K] = { count, countPct, gnwpEur, gnwpEurPct };
        }
        return retval;
    }

    static aggregateAsHistogram(accounts: AccountData[], numBuckets: number, outlierQuantile: number, getValue: (account: AccountData) => number): AccountStatsBucket[] {
        const values = accounts.map(getValue);
        if (values.length === 0) return [];

        const [min, max] = quantileSeq(values, [outlierQuantile, 1.0 - outlierQuantile]) as number[];
        //const min = Math.min(...values);
        //const max = Math.max(...values);

        const isPointLike = min >= max;
        numBuckets = isPointLike ? 1 : numBuckets;
        const bucketSize = isPointLike ? 0.01 : (max - min) / numBuckets;

        return range(numBuckets).map(i => {
            const isFirstBucket = i === 0;
            const isLastBucket = i === values.length - 1;
            const minVal = min + bucketSize * i;
            const maxVal = minVal + bucketSize;
            let count = 0;
            let gnwpEur = 0;
            for (let j = 0; j < values.length; j++) {
                const v = values[j];
                if ((isFirstBucket || v >= minVal) && (isLastBucket || v < maxVal)) {
                    count++;
                    gnwpEur += accounts[j].gnwpEur ?? 0;
                }
            }
            return { min: minVal, max: maxVal, count, gnwpEur };
        });
    }

    static aggregateChecks(accounts: AccountData[]): AccountChecksStats[] {
        const allChecks = accounts.flatMap(a => (a.metadataChecks ?? []) as CheckEvaluation[]);
        const groupedByCheckId = groupBy(allChecks, c => c.id);
        return Object.entries(groupedByCheckId).map(entry => {
            const [id, checks] = entry;
            return { id, name: checks[0].name, severity: checks[0].severity, count: checks.length };
        });
    }

    static fromAccounts(accounts: AccountData[]): PortfolioSegmentStatistics {
        const totalAccounts = Math.max(accounts.length, 0.01);
        const notRenewableAccounts = ArrayUtils.countIf(accounts, a => !a.renewableFlag);
        const renewedAccounts = ArrayUtils.countIf(accounts, a => a.renewalStatus === 'Renewed');
        const lostCancelledAccounts = ArrayUtils.countIf(accounts, a => StringUtils.includes(a.renewalStatus, 'lost', true));
        const newAccounts = ArrayUtils.countIf(accounts, a => StringUtils.isNullOrEmpty(a.uwbId));

        const totalGnwpEur = Math.max(sumBy(accounts, i => i.gnwpEur ?? 0), 0.01);
        const totalRenewedGnwpEur = sumBy(accounts, i => i.renewalGnwp ?? 0)
        const notTieredStatus = AccountSegmentStatisticsCalculator.NotTieredStatus;

        const aggr = AccountSegmentStatisticsCalculator.aggregatePartition.bind(undefined, accounts);
        const partitionByCalculatedTier = (a: AccountData) => a.calculatedPilStatus ?? notTieredStatus;
        const byCalculatedTier = aggr(partitionByCalculatedTier, totalAccounts, totalGnwpEur);

        const partitionByFinalTier = (a: AccountData) => a.finalPilStatus ?? notTieredStatus;
        const byFinalTier = aggr(partitionByFinalTier, totalAccounts, totalGnwpEur);

        const partitionByLastYearTier = (a: AccountData) => a.prevPilStatus ?? notTieredStatus;
        const byLastYearTier = aggr(partitionByLastYearTier, totalAccounts, totalGnwpEur);

        const partitionByHg = (a: AccountData) => a.hazardGrade ?? notTieredStatus;
        const byHg = aggr(partitionByHg, totalAccounts, totalGnwpEur);

        return {
            statsByCalculatedTier: byCalculatedTier,
            statsByFinalTier: byFinalTier,
            statsByLastYearTier: byLastYearTier,
            statsByHg: byHg,
            totalCount: totalAccounts,
            totalGnwpEur: totalGnwpEur,

            renewedGnwpEur: totalRenewedGnwpEur,
            renewedCount: renewedAccounts,

            notRenewableCount: notRenewableAccounts,
            lostCancelledCount: lostCancelledAccounts,
            newCount: newAccounts
        };
    }

    static accountPeriodFilter(year: number | undefined, period: PortfolioPeriodEnum | undefined | null): (a: AccountData) => boolean {
        if (period == null) return () => true;

        const { lower, upper } = AppConstants.ThresholdDates[period];
        const activeYear = year ?? new Date().getFullYear();
        const lowerDate = new Date(activeYear, lower.month, lower.day);
        const upperDate = new Date(activeYear, upper.month, upper.day);
        return (a: AccountData) => {
            const expiryDate = a.expiryDate!;
            if (expiryDate === undefined ) return false;
            return expiryDate >= lowerDate && expiryDate < upperDate;
        }
    }

    static fromPortfolioSegment(pfSegData: PortfolioSegmentDataContext): PortfolioSegmentStatistics {
        return AccountSegmentStatisticsCalculator.fromAccounts(pfSegData.getAccountData());
    }
}
